import { SubcategoryRecommendation } from '@graphql/generated/graphql';

export interface AiRecommendationMetadata {
  recommendedBy?: 'AI';
  reasonCode?: 'HISTORICAL_ASSIGNMENT' | 'TITLE_MATCH' | 'DEFAULT_ASSIGNMENT';
  reasonDetail?: string;
}

/**
 * Applies AI recommendations to items that do not already have a subcategory assigned.
 * Generic over item type so it works with StagedItem, bulk upload rows, or any future item shape.
 *
 * - Items with existing subcategories are left unchanged.
 * - Items without a subcategory that have a matching recommendation get updated.
 * - Items without a subcategory and no matching recommendation remain unchanged.
 */
export function applyRecommendations<T extends { itemId: string; subcategoryId?: string } & AiRecommendationMetadata>(
  items: T[],
  recommendations: SubcategoryRecommendation[],
): T[] {
  const recMap = new Map(recommendations.map((r) => [r.itemId, r]));

  return items.map((item) => {
    // Skip items that already have a subcategory assigned
    if (item.subcategoryId) return item;

    const rec = recMap.get(item.itemId);
    if (!rec) return item;

    return {
      ...item,
      subcategoryId: rec.subcategoryId,
      recommendedBy: 'AI' as const,
      reasonCode: rec.reasonCode as AiRecommendationMetadata['reasonCode'],
      reasonDetail: rec.reasonDetail,
    };
  });
}

/**
 * Returns item IDs where subcategoryId is undefined or empty.
 * Used to determine which items to include in the recommendation request.
 */
export function getItemsWithoutSubcategory<T extends { itemId: string; subcategoryId?: string }>(items: T[]): string[] {
  return items
    .filter((item) => item.subcategoryId === undefined || item.subcategoryId === '')
    .map((item) => item.itemId);
}

/**
 * Returns true if the array is non-empty AND at least one item has no subcategoryId.
 * Used to determine whether the AI Suggest button should be enabled.
 */
export function isAiSuggestEnabled<T extends { subcategoryId?: string }>(items: T[]): boolean {
  return items.length > 0 && items.some((item) => !item.subcategoryId);
}

/**
 * Returns item with recommendedBy, reasonCode, reasonDetail set to undefined.
 * Used when user manually overrides an AI suggestion.
 */
export function clearAiMetadata<T extends AiRecommendationMetadata>(item: T): T {
  return {
    ...item,
    recommendedBy: undefined,
    reasonCode: undefined,
    reasonDetail: undefined,
  };
}
